import {
	Button,
	FormControl,
	FormHelperText,
	Grid2,
	TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
	Controller,
	type SubmitHandler,
	useForm,
	useWatch,
} from "react-hook-form";
import { ErrorModal } from "../../../../components/modals/error-modal";
import MesfModal from "../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../components/shared/buttons/button-with-loading";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "../../../../controls/snackbars/notification-snackbar";
import { HelmetDexteel } from "../../../../helmet/HelmetDexteel";
import { useEmailTopics } from "../../emailTopics/EmailTopicsPage";
import {
	type EmailAddress,
	EmailAddressFormValues,
	INITIAL_VALUES,
} from "../../emailTopics/models/EmailAddress";
import {
	deleteEmailAddress,
	setEmailAddressTopics,
	upsertEmailAddress,
} from "../../emailTopics/repositories/EmailTopicsRepository";
import { AssignedTopicsTable, TopicField } from "./AssignedTopicsTable";

const isValidEmail = (email: string): boolean => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email.trim());
};

type Props = {
	modal: "" | "create" | "update" | "delete";
	onHide: (shouldUpdate: boolean) => void;
	address: EmailAddress | null;
	includeSystem: boolean;
};

export const UpsertDeleteModal = ({
	modal,
	onHide,
	address,
	includeSystem,
}: Props) => {
	const queryClient = useQueryClient();

	const [message, setMessage] = useState("");
	const [error, setError] = useState("");
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

	const {
		data: topicsData,
		isLoading: topicsLoading,
		error: topicsError,
	} = useEmailTopics({ includeSystem, enabled: modal !== "" });

	const topics = topicsData?.topics ?? [];
	const assignedTopics = topicsData?.addresses ?? [];

	const { control, getValues, setValue, reset, handleSubmit } =
		useForm<EmailAddressFormValues>({
			defaultValues: INITIAL_VALUES,
		});

	const selectedTO = useWatch({ control, name: "TopicIdsTO" });
	const selectedCC = useWatch({ control, name: "TopicIdsCC" });
	const selectedBCC = useWatch({ control, name: "TopicIdsBCC" });

	const selectedByField: Record<TopicField, number[]> = {
		TopicIdsTO: selectedTO ?? [],
		TopicIdsCC: selectedCC ?? [],
		TopicIdsBCC: selectedBCC ?? [],
	};

	const toggleTopic = (
		field: TopicField,
		emailTopicId: number,
		checked: boolean,
	) => {
		const current = getValues(field) ?? [];

		setValue(
			field,
			checked
				? [...current, emailTopicId]
				: current.filter((id) => id !== emailTopicId),
			{ shouldDirty: true },
		);
	};

	const deleteAddressMutation = useMutation({
		mutationFn: (emailAddressId: number) => deleteEmailAddress(emailAddressId),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["emailAddresses"] }),
				queryClient.invalidateQueries({ queryKey: ["emailTopics"] }),
			]);
			setMessage("The email address was deleted succesfully");
			onHide(true);
		},
		onError: (mutationError: Error) => {
			setError(mutationError.message);
		},
	});

	const upsertAddressMutation = useMutation({
		mutationFn: async (data: EmailAddressFormValues) => {
			const emailAddressId = await upsertEmailAddress({
				EmailAddressId: data.EmailAddressId,
				Address: data.Address,
			});

			const targetId = data.EmailAddressId ?? emailAddressId;

			if (targetId == null) {
				throw new Error("The email address could not be identified after save");
			}

			await setEmailAddressTopics({
				EmailAddressId: targetId,
				TopicIdsTO: data.TopicIdsTO,
				TopicIdsCC: data.TopicIdsCC,
				TopicIdsBCC: data.TopicIdsBCC,
				IncludeSystem: includeSystem,
			});
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["emailAddresses"] }),
				queryClient.invalidateQueries({ queryKey: ["emailTopics"] }),
			]);
			setMessage(
				`The email address was ${modal === "update" ? "updated" : "created"} succesfully`,
			);
			onHide(true);
		},
		onError: (mutationError: Error) => {
			setError(mutationError.message);
		},
	});

	const assignedTopicNames = Array.from(
		new Set(
			[address?.TopicsTO, address?.TopicsCC, address?.TopicsBCC]
				.flatMap((topics) => (topics ?? "").split(","))
				.map((topic) => topic.trim())
				.filter(Boolean),
		),
	);

	const onSubmit: SubmitHandler<EmailAddressFormValues> = async (data) => {
		if (modal === "delete") {
			if (data.EmailAddressId != null) {
				setIsConfirmingDelete(true);
			}

			return;
		}

		await upsertAddressMutation.mutateAsync(data);
	};

	const onDeleteConfirmationClose = (value?: string) => {
		setIsConfirmingDelete(false);

		if (value === "OK" && address) {
			deleteAddressMutation.mutate(address.EmailAddressId);
		}
	};

	useEffect(() => {
		if (modal === "create" || !address) {
			reset(INITIAL_VALUES);
			return;
		}

		const idsOfType = (recipientType: string) =>
			assignedTopics
				.filter(
					(assignment) =>
						assignment.EmailAddressId === address.EmailAddressId &&
						assignment.RecipientType === recipientType,
				)
				.map((assignment) => assignment.EmailTopicId);

		reset({
			EmailAddressId: address.EmailAddressId,
			Address: address.Address,
			TopicIdsTO: idsOfType("TO"),
			TopicIdsCC: idsOfType("CC"),
			TopicIdsBCC: idsOfType("BCC"),
		});
	}, [address, assignedTopics, modal]);

	const isAddressBusy =
		upsertAddressMutation.isPending || deleteAddressMutation.isPending;

	return (
		<>
			{modal !== "" ? (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: modal === "update"
								? "Edit"
								: modal === "delete"
									? "Delete"
									: ""
					} Email Address`}
				/>
			) : null}
			<MesfModal
				title={`${
					modal === "update" ? "EDIT" : modal === "delete" ? "DELETE" : "CREATE"
				} EMAIL ADDRESS`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="md"
				id="email-address-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						style={{ padding: "10px 30px", minWidth: "50vw" }}
					>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="Address"
									control={control}
									rules={{
										required: "Address is required",
										validate: (value) =>
											isValidEmail(value) || "Invalid email address",
									}}
									render={({
										field: { onChange, value },
										fieldState: { error: fieldError },
									}) => (
										<FormControl error={!!fieldError} fullWidth>
											<TextField
												label="Address"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												placeholder="name@company.com"
												value={value}
												disabled={modal === "delete" || isAddressBusy}
												onChange={onChange}
											/>
											{fieldError ? (
												<FormHelperText>{fieldError.message}</FormHelperText>
											) : null}
										</FormControl>
									)}
								/>
							</Grid2>
							{modal !== "delete" ? (
								<Grid2 size={{ xs: 12 }}>
									<AssignedTopicsTable
										topics={topics}
										isLoading={topicsLoading}
										error={topicsError}
										selectedByField={selectedByField}
										onToggle={toggleTopic}
										onError={setError}
										disabled={isAddressBusy || topicsLoading}
									/>
								</Grid2>
							) : null}
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isAddressBusy}
									variant="contained"
									color={modal === "delete" ? "secondary" : "primary"}
									type="submit"
								>
									{modal === "delete" ? "Delete" : "Save"}
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ConfirmationDialogRaw
				open={isConfirmingDelete}
				onClose={onDeleteConfirmationClose}
				title="Delete Confirmation"
				suffixTitle="Email Address"
				message={
					<>
						You are about to delete the email address <b>{address?.Address}</b>.
						{assignedTopicNames.length > 0 ? (
							<>
								{" "}
								It is currently assigned to {assignedTopicNames.length} topic
								{assignedTopicNames.length > 1 ? "s" : ""} (
								<b>{assignedTopicNames.join(", ")}</b>), and those assignments
								will be removed as well.
							</>
						) : null}{" "}
						Are you sure you want to proceed?
					</>
				}
			/>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In Email Address"
			/>
		</>
	);
};
